import { useFormikContext } from 'formik';
import { isEqual } from 'lodash';

import { Box } from '@hivebrite/design-system/Box';
import { Card } from '@hivebrite/design-system/Card';

import { useDefaultAppContexts } from 'hb-react/frontoffice/contexts/withDefaultAppContexts';
import { useJobContext } from 'hb-react/shared/apps/JobForm/contexts/JobContext';
import { Col, Grid, Row } from 'hb-react/shared/components/DesignSystem/Grid';
import RequiredExplanation from 'hb-react/shared/components/formik/components/RequiredExplanation';
import { IsActive } from 'hb-react/shared/components/IsActive';

import { Company } from './Company';
import { Kind } from './Kind';
import { LinkedCompany } from './LinkedCompany';
import { Location } from './Location';
import { Logo } from './Logo';
import { Remote } from './Remote';
import { Status } from './Status';
import { Title } from './Title';
import { type FormValues } from '../types';

interface GeneralInfoProps {
  allowLinkToOrg?: boolean;
  isSignedIn: boolean;
  topicId: number | undefined;
}

export const GeneralInfo = ({
  allowLinkToOrg = false,
  isSignedIn,
  topicId,
}: GeneralInfoProps) => {
  const { currentUser } = useDefaultAppContexts();

  const { values } = useFormikContext<FormValues>();

  const { kindConfigurations = [] } = useJobContext();

  const { fields } =
    kindConfigurations.find(({ kind_id: kindId }) =>
      isEqual(kindId, values.kind_id),
    ) || {};

  return (
    <Card my={{ _: 0, m: 6 }} pb={0} noBorderRadiusOnSmallScreen>
      <Grid columnsInMobile mx={0}>
        <Row>
          <Col overrideRowGap={4}>
            <Box as="h3" font="h3-semibold">
              {s__('JobForm|General information')}
            </Box>
          </Col>
          <Status />
        </Row>
        <Row>
          <Col overrideRowGap={0}>
            <RequiredExplanation />
          </Col>
        </Row>
        <Row>
          <Col overrideRowGap={4}>
            <Box flexDirection={{ m: 'row', s: 'column' }}>
              <Logo />
              <Box flexDirection="column" flexGrow={1}>
                <Kind currentUser={currentUser} />
                <Title />
                {/* Cannot link group opportunities because of group page display permissions */}
                {(isSignedIn || allowLinkToOrg) && topicId === undefined ? (
                  <IsActive isActive={fields?.company_name}>
                    <LinkedCompany />
                  </IsActive>
                ) : (
                  <IsActive isActive={fields?.company_name}>
                    <Company />
                  </IsActive>
                )}
                <IsActive isActive={fields?.location}>
                  <Location />
                </IsActive>
                <Remote />
              </Box>
            </Box>
          </Col>
        </Row>
      </Grid>
    </Card>
  );
};
